import { z, ZodError } from 'zod'
import { oloApi } from '@/lib/apiClient'
import { oloUrls } from '@/lib/urlBuilder'
import { normalizeZodError } from '@/lib/apiErrors'

// ---------------------------------------------------------------------------
// Modifier tree — mutually recursive, requires z.lazy() + explicit ZodType<T>
// Ember equivalents: app/models/modifier.js, app/models/modifier-set.js
// ---------------------------------------------------------------------------

export interface MenuModifier {
	ref: string
	name: string
	description: string | null
	price: number
	percent: number
	modifierSets: MenuModifierSet[]
}

export interface MenuModifierSet {
	ref: string
	name: string
	description: string | null
	minModifiersPerSet: number
	maxModifiersPerSet: number
	modifiers: MenuModifier[]
}

export const MenuModifierSchema: z.ZodType<MenuModifier> = z.lazy(() =>
	z.object({
		ref: z.string(),
		name: z.string(),
		description: z.string().nullable(),
		price: z.number(),
		percent: z.number().optional().default(0),
		modifierSets: z.array(MenuModifierSetSchema),
	}),
)

export const MenuModifierSetSchema: z.ZodType<MenuModifierSet> = z.lazy(() =>
	z.object({
		ref: z.string(),
		name: z.string(),
		description: z.string().nullable(),
		minModifiersPerSet: z.number(),
		maxModifiersPerSet: z.number(),
		modifiers: z.array(MenuModifierSchema),
	}),
)

// ---------------------------------------------------------------------------
// Menu item detail — includes modifierSets; fetched when opening the item modal
// Ember equivalent: item-modal route model hook → store.findRecord('item', guid)
// ---------------------------------------------------------------------------

export const MenuItemDetailSchema = z.object({
	ref: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	price: z.number(),
	onlinePrice: z.number().nullable().optional(),
	imageUrl800x800: z.string().nullable(),
	stockStatus: z.enum(['OUT_OF_STOCK', 'LOW_STOCK']).nullable(),
	modifierSets: z.array(MenuModifierSetSchema),
})
export type MenuItemDetail = z.infer<typeof MenuItemDetailSchema>

export const MenuCategorySchema = z.object({
	ref: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	sortIndex: z.number().optional(),
	items: z.array(z.string()),
})
export type MenuCategory = z.infer<typeof MenuCategorySchema>

export const MenuCategoriesResponseSchema = z.object({
	categories: z.array(MenuCategorySchema),
})
export type MenuCategoriesResponse = z.infer<typeof MenuCategoriesResponseSchema>

export const MenuItemSchema = z.object({
	ref: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	price: z.number(),
	onlinePrice: z.number().nullable().optional(),
	imageUrl800x800: z.string().nullable(),
	stockStatus: z.enum(['OUT_OF_STOCK', 'LOW_STOCK']).nullable(),
})
export type MenuItem = z.infer<typeof MenuItemSchema>

export const MenuItemsResponseSchema = z.object({
	items: z.array(MenuItemSchema),
})
export type MenuItemsResponse = z.infer<typeof MenuItemsResponseSchema>

export function getMenuCategories(
	ref: string,
	scheduleId: string,
): Promise<MenuCategoriesResponse> {
	return oloApi
		.get<unknown>(oloUrls.menuCategories(ref, scheduleId))
		.then((raw) => MenuCategoriesResponseSchema.parse(raw))
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}

export function getMenuItems(ref: string, scheduleId: string): Promise<MenuItemsResponse> {
	return oloApi
		.get<unknown>(oloUrls.menuItems(ref, scheduleId))
		.then((raw) => MenuItemsResponseSchema.parse(raw))
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}

const ApiMenuItemDetailResponseSchema = z.object({ item: MenuItemDetailSchema })

// staleTime must be 0 at the call site — Ember used reload:true on every route visit.
export function getMenuItemDetail(
	ref: string,
	guid: string,
	dineIn?: boolean,
): Promise<MenuItemDetail> {
	const base = oloUrls.menuItemDetail(ref, guid)
	const url = dineIn ? `${base}?dineIn=true` : base
	return oloApi
		.get<unknown>(url)
		.then((raw) => ApiMenuItemDetailResponseSchema.parse(raw).item)
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}
